import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { motion } from 'framer-motion';

const ExperienceTimeline = ({ experiences, onExperienceSelect }) => {
  return (
    <div className="relative">
      {/* Desktop Experience Cards (no timeline line/dot) */}
      <div className="hidden lg:block">
        <div className="space-y-10 py-12">
          {experiences.map((experience, index) => (
            <motion.div
              key={experience.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="mx-auto"
            >
              <div className="bg-surface rounded-2xl border border-border p-8 shadow-sm hover:shadow-lg transition-all duration-300">
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-text-primary mb-2">
                    {experience.role}
                  </h3>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-primary font-medium text-sm">
                      <Icon name="Building" size={16} className="mr-2" />
                      {experience.company}
                    </div>
                    <div className="flex items-center text-text-secondary text-sm">
                      <Icon name="Calendar" size={16} className="mr-2" />
                      {experience.duration}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-border">
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-semibold text-text-primary mb-3">
                        Key Responsibilities:
                      </h4>
                      <ul className="text-sm text-text-secondary space-y-2">
                        {experience.responsibilities.map((resp, idx) => (
                          <li key={idx} className="flex items-start">
                            <Icon
                              name="ChevronRight"
                              size={14}
                              className="mr-2 mt-0.5 text-primary flex-shrink-0"
                            />
                            <p className="text-base flex items-center text-text-secondary">{resp}</p>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-text-primary mb-3">
                        Technologies Used:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {experience.technologies.map((tech, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-primary/10 text-xs rounded-full text-primary border border-primary/20"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Mobile/Tablet Vertical Timeline */}
      <div className="lg:hidden">
        <div className="relative">
          {/* Vertical Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border"></div>

          <div className="space-y-8">
            {experiences.map((experience, index) => (
              <motion.div
                key={experience.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative flex items-start"
              >
                {/* Timeline Dot */}
                <div className="absolute left-8 transform -translate-x-1/2 w-4 h-4 bg-primary rounded-full border-4 border-background z-10"></div>

                {/* Experience Card */}
                <div className="ml-16 bg-surface rounded-lg border border-border p-6 shadow-sm hover:shadow-md transition-all duration-300 w-full">
                  <div className="mb-3">
                    <h3 className="font-semibold text-text-primary mb-1">{experience.role}</h3>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-primary font-medium text-xs">
                        <Icon name="Building" size={14} className="mr-2" />
                        {experience.company}
                      </div>
                      <div className="flex items-center text-text-secondary text-xs">
                        <Icon name="Calendar" size={12} className="mr-1" />
                        {experience.duration}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1 mt-3">
                    {experience.technologies.slice(0, 3).map((tech, idx) => (
                      <span key={idx} className="px-2 py-1 bg-muted text-xs rounded-md text-text-secondary">
                        {tech}
                      </span>
                    ))}
                    {experience.technologies.length > 3 && (
                      <span className="px-2 py-1 bg-muted text-xs rounded-md text-text-secondary">
                        +{experience.technologies.length - 3}
                      </span>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-medium text-text-primary mb-2">Key Responsibilities:</h4>
                        <ul className="text-sm text-text-secondary space-y-2">
                          {experience.responsibilities.map((resp, idx) => (
                            <li key={idx} className="flex items-start">
                              <Icon name="ChevronRight" size={14} className="mr-2 mt-0.5 text-primary flex-shrink-0" />
                              {resp}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium text-text-primary mb-2">Key Achievements:</h4>
                        {Array.isArray(experience.achievements) && experience.achievements.length > 0 && (
                          <ul className="text-sm text-text-secondary space-y-2">
                            {experience.achievements.map((achievement, idx) => (
                              <li key={idx} className="flex items-start">
                                <Icon name="Award" size={14} className="mr-2 mt-0.5 text-accent flex-shrink-0" />
                                {achievement}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <div>
                        <h4 className="text-sm font-medium text-text-primary mb-2">Technologies Used:</h4>
                        <div className="flex flex-wrap gap-2">
                          {experience.technologies.map((tech, idx) => (
                            <span key={idx} className="px-3 py-1 bg-primary/10 text-sm rounded-full text-primary border border-primary/20">
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>

                      {experience.projectLink && (
                        <div className="pt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="ExternalLink"
                            iconPosition="right"
                            onClick={() => onExperienceSelect(experience)}
                          >
                            View Related Projects
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperienceTimeline;